import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import alertify from 'alertifyjs';
import { environment } from '../../../environments/environment';
import { ApiServiceService } from './api-service.service';
import { AuthenticationService } from './authentication.service';

export interface WorkflowNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  recipientUserId: string;
  applicationId?: string;
  workflowInstanceId?: string;
  applicationRef?: string;
  workflowName?: string;
  mdaName?: string;
  actorName?: string;
  comment?: string;
  read: boolean;
  createdAt: string;
  readAt?: string;
  payload?: Record<string, unknown>;
}

export interface NotificationListResponse {
  content: WorkflowNotification[];
  total: number;
  page: number;
  size: number;
}

@Injectable({
  providedIn: 'root',
})
export class WorkflowNotificationService {
  private socket: WebSocket | null = null;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private manualClose = false;
  private readonly workflowNotificationSubject = new Subject<WorkflowNotification>();

  workflowNotifications$: Observable<WorkflowNotification> =
    this.workflowNotificationSubject.asObservable();

  constructor(
    private readonly apiService: ApiServiceService,
    private readonly authenticationService: AuthenticationService,
  ) {}

  connect(): void {
    if (!this.authenticationService.isAuthenticated()) return;
    if (
      this.socket &&
      (this.socket.readyState === WebSocket.OPEN ||
        this.socket.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    this.manualClose = false;
    this.socket = new WebSocket(environment.notification_ws_url);

    this.socket.onopen = () => {
      this.reconnectAttempt = 0;
    };

    this.socket.onmessage = (event) => {
      this.handleMessage(event.data);
    };

    this.socket.onclose = () => {
      this.socket = null;
      if (!this.manualClose && this.authenticationService.isAuthenticated()) {
        this.scheduleReconnect();
      }
    };

    this.socket.onerror = () => {
      this.socket?.close();
    };
  }

  disconnect(): void {
    this.manualClose = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.socket?.close();
    this.socket = null;
  }

  fetchNotifications(page = 1, pageSize = 20, unreadOnly = false): Observable<any> {
    return this.apiService.get('/notifications', {
      params: {
        page,
        pageSize,
        unreadOnly,
      },
    });
  }

  fetchUnreadCount(): Observable<any> {
    return this.apiService.get('/notifications/unread-count');
  }

  markRead(id: string): Observable<any> {
    return this.apiService.patch(`/notifications/${id}/read`, {});
  }

  markAllRead(): Observable<any> {
    return this.apiService.patch('/notifications/read-all', {});
  }

  private handleMessage(raw: string): void {
    try {
      const notification = JSON.parse(raw) as WorkflowNotification;
      if (!notification?.type?.startsWith('workflow.')) return;

      alertify.message(notification.message || notification.title || 'Workflow notification');
      this.workflowNotificationSubject.next(notification);
    } catch {
      // Ignore malformed socket messages.
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;

    const delay = Math.min(30000, 1000 * Math.pow(2, this.reconnectAttempt));
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }
}
